import React, { useState } from 'react';

import { BadRequestError } from '_lib/api/apiErrors';
import { Customer, CustomerCreateDTO } from '_lib/api/apiTypes.shim';
import { ServiceTitanCustomerResource } from '_lib/api/resources/serviceTitanCustomerResource';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { CustomerForm } from '_lib/featureComponents/Customer/CustomerForm';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { ApiSubmitHandler } from '_lib/types/hookForm.types';
import { reportException } from '_lib/utils/sentryService';

interface CustomerFormDialogProps extends AsyncDialogProps<{ customer: Customer }> {
  customer: Customer;
  customerResource: ServiceTitanCustomerResource;
}

const CustomerFormDialog = (props: CustomerFormDialogProps) => {
  const { customer, customerResource, onClose, titleText, ...dialogProps } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[] | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit: ApiSubmitHandler<CustomerCreateDTO> = async (formCustomer, { setFormErrors }) => {
    setIsSaving(true);
    setNonFieldErrors(undefined);

    try {
      // TODO - pick out customer patch fields until we can refactor CustomerForm to be a generic with support for
      //        our create and update use-cases
      const { locations, contacts, notes, useAddressForLocation, ...customerPatchDto } = formCustomer;
      const updatedCustomer = await customerResource.patch(customer.providerId, customerPatchDto);

      onClose({
        action: 'CONFIRM',
        value: { customer: updatedCustomer }
      });
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogWrapper onClose={onClose} titleText={titleText || 'Complete Missing Customer Info'} {...dialogProps}>
      <DialogContent dividers>
        <BackdropSpinner open={isSaving} />
        {nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ marginTop: 1, borderRadius: 4 }} />}
        <CustomerForm
          onCustomerSubmit={handleSubmit}
          customer={customer}
          formId='st-api-call-pop-customer-form-dialog'
        />
      </DialogContent>
      <DialogActions>
        <DialogButton type='submit' form='st-api-call-pop-customer-form-dialog' text='Save' />
      </DialogActions>
    </DialogWrapper>
  );
};

interface CustomerFormDialog extends AsyncDialog<{ customer: Customer }, CustomerFormDialogProps> {}
export const useCustomerFormDialog = (): CustomerFormDialog => useAsyncDialog(CustomerFormDialog);
